import { API_BASE_URL, fetchApi } from '@/services/api-client';

export const INCIDENTS_PATH = '/incidents';
export const ADMIN_INCIDENTS_PATH = '/admin/incidents';

export type IncidentType =
  | 'biting_ey'
  | 'hitting_ey'
  | 'pushing_ey'
  | 'throwing_objects'
  | 'verbal_aggression'
  | 'targeted_harm'
  | 'inappropriate_language'
  | 'property_damage'
  | 'leaving_supervised_areas'
  | 'emotional_dysregulation'
  | 'accident'
  | 'peer_conflict'
  | 'other';
export type IncidentSeverity = 'low' | 'medium' | 'high';
export type IncidentStatus = 'open' | 'investigating' | 'closed';
export type IncidentStudentRole = 'initiated' | 'affected' | 'witness' | 'unclear';
export type IncidentOutcome =
  | 'no_further_action'
  | 'monitoring'
  | 'parents_informed'
  | 'behaviour_support_plan'
  | 'referred_to_counsellor'
  | 'disciplinary_action'
  | 'other';
export type IncidentEventKind = 'message' | 'status_change' | 'decision' | 'participant_change' | 'opened';
/** `sst_only` events never reach a non-SST caller's response at all — see the API's IncidentMessagesService. */
export type IncidentEventVisibility = 'staff' | 'sst_only';

export type IncidentAttachment = {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
};

/** English-only source of truth for the role set; display via t('enum.incident_student_role.' + value). */
export const INCIDENT_STUDENT_ROLE_LABELS: Record<IncidentStudentRole, string> = {
  initiated: 'Initiated / caused harm',
  affected: 'Affected / harmed',
  witness: 'Witness',
  unclear: 'Involved — unclear',
};

/** English-only source of truth for the outcome set; display via t('enum.incident_outcome.' + value). */
export const INCIDENT_OUTCOME_LABELS: Record<IncidentOutcome, string> = {
  no_further_action: 'No further action',
  monitoring: 'Monitoring',
  parents_informed: 'Parents informed',
  behaviour_support_plan: 'Behaviour support plan',
  referred_to_counsellor: 'Referred to counsellor',
  disciplinary_action: 'Disciplinary action',
  other: 'Other',
};

export type IncidentStudent = {
  studentId: string;
  displayName: string;
  /** The name on the register, shown under the nickname where the two differ. */
  fullName: string | null;
  photoUrl: string;
  gradeLevel: number;
  role: IncidentStudentRole;
  note: string | null;
  underSstSupervision: boolean;
};

export type IncidentStaff = {
  userId: string;
  displayName: string;
  photoUrl: string;
  email: string | null;
  isReporter: boolean;
};

export type IncidentEvent = {
  id: string;
  kind: IncidentEventKind;
  author: { id: string; displayName: string; photoUrl: string } | null;
  body: string | null;
  fromStatus: IncidentStatus | null;
  toStatus: IncidentStatus | null;
  visibility: IncidentEventVisibility;
  createdAt: string;
};

export type IncidentScheduleContext = {
  studentId: string;
  className: string;
  periodName: string;
  startTime: string;
  endTime: string;
  subjectName: string | null;
  teacherName: string | null;
  roomName: string | null;
};

export type Incident = {
  id: string;
  students: IncidentStudent[];
  staff: IncidentStaff[];
  reporterId: string | null;
  reporterEmail: string | null;
  reporterName: string | null;
  /** The name on file and the name they go by, kept apart so a list can show both. */
  reporterFullName: string | null;
  reporterNickname: string | null;
  incidentDate: string;
  incidentTime: string | null;
  location: string;
  type: IncidentType;
  typeOther: string | null;
  legacyStudentsInvolved: string | null;
  legacyStaffInvolved: string | null;
  severity: IncidentSeverity;
  medicalAttention: boolean;
  /** SST viewers only — absent for anyone else, not merely empty. */
  infirmaryVisits?: IncidentInfirmaryVisit[];
  description: string;
  actionTaken: string;
  preventionSuggestion: string;
  status: IncidentStatus;
  outcome: IncidentOutcome | null;
  decisionNote: string | null;
  /** Absent unless the viewer is SST — check with `'decisionPrivateNote' in incident`. */
  decisionPrivateNote?: string | null;
  closedById: string | null;
  closedByName: string | null;
  closedAt: string | null;
  events?: IncidentEvent[];
  scheduleContexts?: IncidentScheduleContext[];
  unreadCount: number;
  /** True until the viewer opens a case someone else filed; never true for the reporter. */
  isNew: boolean;
  attachments: IncidentAttachment[];
  createdAt: string;
};

export type CreateIncidentData = {
  students: { studentId: string; role: IncidentStudentRole; note?: string }[];
  staffIds?: string[];
  incidentDate: string;
  incidentTime?: string;
  location: string;
  type: IncidentType;
  typeOther?: string;
  severity: IncidentSeverity;
  medicalAttention: boolean;
  description: string;
  actionTaken: string;
  preventionSuggestion: string;
};

/**
 * PATCH /incidents/:id — the report fields only. Participants and attachments have their own
 * endpoints. `null` is meaningful on the two nullable columns: it clears the stored value, which is
 * how the edit form drops a time or a free-text type. Every other field rejects an explicit null.
 */
export type UpdateIncidentData = {
  incidentDate?: string;
  incidentTime?: string | null;
  location?: string;
  type?: IncidentType;
  typeOther?: string | null;
  severity?: IncidentSeverity;
  medicalAttention?: boolean;
  description?: string;
  actionTaken?: string;
  preventionSuggestion?: string;
};

export type CloseIncidentData = { outcome: IncidentOutcome; decisionNote: string; privateNote?: string };

export type AdminIncidentFilters = {
  status?: IncidentStatus;
  studentId?: string;
  severity?: IncidentSeverity;
  search?: string;
};

/**
 * English-only source of truth for the set of incident types (and their exhaustiveness via
 * `Record<IncidentType, string>`). Used to derive the option list / validation set in
 * ReportIncidentDialog — for display, use `t('enum.incident_type.' + value)` instead.
 */
export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  biting_ey: 'Biting (EY)',
  hitting_ey: 'Hitting (EY)',
  pushing_ey: 'Pushing (EY)',
  throwing_objects: 'Throwing objects',
  verbal_aggression: 'Verbal Aggression',
  targeted_harm: 'Targeted harmful behavior towards an individual',
  inappropriate_language: 'Inappropriate language, gestures, or written representations',
  property_damage: 'Property related damage',
  leaving_supervised_areas: 'Leaving supervised areas',
  emotional_dysregulation: 'Emotional dysregulation',
  accident: 'Accident',
  peer_conflict: 'Peer conflict',
  other: 'Other',
};

/**
 * English-only source of truth for the set of incident severities. Used to derive the option
 * list / validation set in ReportIncidentDialog — for display, use
 * `t('enum.incident_severity.' + value)` instead.
 */
export const INCIDENT_SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export type IncidentInfirmaryVisit = {
  id: string;
  studentId: string;
  studentName: string;
  visitDate: string;
  visitTime: string;
  typeName: string;
};

export const incidentsApi = {
  listMine: () => fetchApi<Incident[]>(`${INCIDENTS_PATH}/mine`),
  get: (id: string) => fetchApi<Incident>(`${INCIDENTS_PATH}/${id}`),
  report: (data: CreateIncidentData) =>
    fetchApi<Incident>(INCIDENTS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateIncidentData) =>
    fetchApi<Incident>(`${INCIDENTS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  postMessage: (id: string, body: string, visibility?: IncidentEventVisibility) =>
    fetchApi<Incident>(`${INCIDENTS_PATH}/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, visibility }),
    }),
  linkableInfirmaryVisits: (id: string) =>
    fetchApi<IncidentInfirmaryVisit[]>(`${INCIDENTS_PATH}/${id}/infirmary-visits/linkable`),
  setInfirmaryVisitLink: (id: string, visitId: string, linked: boolean) =>
    fetchApi<Incident>(`${INCIDENTS_PATH}/${id}/infirmary-visits/${visitId}`, {
      method: linked ? 'POST' : 'DELETE',
    }),
  markRead: (id: string) => fetchApi<{ ok: true }>(`${INCIDENTS_PATH}/${id}/read`, { method: 'POST' }),
  unreadCount: () => fetchApi<{ count: number }>(`${INCIDENTS_PATH}/unread-count`),
  setStudents: (id: string, students: CreateIncidentData['students']) =>
    fetchApi<Incident>(`${INCIDENTS_PATH}/${id}/students`, { method: 'PUT', body: JSON.stringify({ students }) }),
  setStaff: (id: string, staffIds: string[]) =>
    fetchApi<Incident>(`${INCIDENTS_PATH}/${id}/staff`, { method: 'PUT', body: JSON.stringify({ staffIds }) }),
  adminList: (filters: AdminIncidentFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.append('status', filters.status);
    if (filters.studentId) qs.append('studentId', filters.studentId);
    if (filters.severity) qs.append('severity', filters.severity);
    if (filters.search) qs.append('search', filters.search);
    const s = qs.toString();
    return fetchApi<Incident[]>(`${ADMIN_INCIDENTS_PATH}${s ? `?${s}` : ''}`);
  },
  changeStatus: (id: string, status: 'investigating') =>
    fetchApi<Incident>(`${ADMIN_INCIDENTS_PATH}/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  close: (id: string, data: CloseIncidentData) =>
    fetchApi<Incident>(`${ADMIN_INCIDENTS_PATH}/${id}/close`, { method: 'POST', body: JSON.stringify(data) }),
  updateDecision: (id: string, data: Partial<CloseIncidentData>) =>
    fetchApi<Incident>(`${ADMIN_INCIDENTS_PATH}/${id}/decision`, { method: 'PATCH', body: JSON.stringify(data) }),
  uploadAttachments: async (incidentId: string, files: File[]): Promise<Incident> => {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    const res = await fetch(`${API_BASE_URL}${INCIDENTS_PATH}/${incidentId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return res.json() as Promise<Incident>;
  },
  deleteAttachment: (attachmentId: string) =>
    fetchApi<{ ok: true }>(`${INCIDENTS_PATH}/attachments/${attachmentId}`, { method: 'DELETE' }),
};

/** Fetch a private attachment as a blob (the endpoint is auth-gated, so a plain <a href> won't carry the cookie). */
export async function downloadIncidentAttachment(attachment: IncidentAttachment): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${INCIDENTS_PATH}/attachments/${attachment.id}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to download attachment');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = attachment.originalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
